/**
 * Route guard for the back-half clinical workbench
 * (BACK-HALF-CLINICAL-WORKBENCH-UI-01). Mirrors RequireWorkboard: the pure
 * decision lives in decideClinicalWorkbenchAccess (clinical-workbench-view.ts)
 * and keys off the caller's role - coc / doctor (the outcome recorders) and
 * owner (the revenue + follow-up view). Any other role redirects to its own
 * homePath. This is a role-only gate: NO /me flag signal, NO feature flag, NO
 * new nav section (internal surface, default-on per ADR-0016).
 *
 * Backend route auth (the /api/appointments/:id/{treatment-*} OUTCOME role gate
 * and the /api/leads/:id/stage STAGE_CONTRACTS role authority) remains the real
 * security boundary; this guard is UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideClinicalWorkbenchAccess } from '@/dashboard/clinical-workbench/clinical-workbench-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireClinicalWorkbenchViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireClinicalWorkbenchView({
  loading,
  error,
  me,
  children,
}: RequireClinicalWorkbenchViewProps): JSX.Element | null {
  const decision = decideClinicalWorkbenchAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireClinicalWorkbench(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireClinicalWorkbenchView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
